/* eslint-disable @typescript-eslint/no-unused-vars */
// src\pages\dashboard\AdminPackagesManager.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { usePackageManagement } from "../../context/ApplicationContext";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  SkeletonAdminPackageStats,
  SkeletonAdminPackageSearch,
  SkeletonAdminPackageCard,
  SkeletonAdminPackageForm,
} from "../../components/Skeleton";

const AdminPackagesManager = () => {
  const {
    packages,
    packageStats,
    packageLoading,
    packageError,
    packageSuccessMessage,
    refreshPackages,
    refreshPackageStats,
    // createPackage,
    updatePackage,
    deletePackage,
    deactivatePackage,
    togglePackageStatus,
    togglePopularStatus,
    restorePackage,
    getSubscriptionCount,
  } = usePackageManagement();

  const { isDark } = useDarkMode();

  const [editingPackage, setEditingPackage] = useState<any | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [packageTypeFilter, setPackageTypeFilter] = useState<
    "all" | "whitelist_request" | "job_seeker"
  >("all");
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [initialLoadAttempted, setInitialLoadAttempted] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetch packages and stats on component mount
  useEffect(() => {
    const initializeData = async () => {
      if (!initialLoadAttempted) {
        setInitialLoadAttempted(true);
        try {
          await refreshPackages();
          await refreshPackageStats();
          setLoadError(null);
        } catch (error) {
          console.error("Failed to initialize packages:", error);
          setLoadError("Failed to load packages. Please try refreshing.");
        }
      }
    };

    initializeData();
  }, [initialLoadAttempted, refreshPackages, refreshPackageStats]);

  // Add a manual retry function
  const handleRetryLoad = async () => {
    setLoadError(null);
    setInitialLoadAttempted(false); // This will trigger the useEffect again
  };

  // Handle refresh action
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshPackages();
      await refreshPackageStats();
      setLoadError(null);
    } catch (error) {
      console.error("Error refreshing data:", error);
      setLoadError("Failed to refresh packages. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  // Ensure packages is always an array for filtering
  const safePackages = Array.isArray(packages) ? packages : [];

  // Filter packages based on search term, type, and activation status
  const filteredPackages = safePackages.filter(
    (pkg) =>
      (packageTypeFilter === "all" || pkg.type === packageTypeFilter) &&
      (showDeactivated ? !pkg.isActive : pkg.isActive) &&
      (pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.description?.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  // Separate active and deactivated packages for stats
  const deactivatedPackages = safePackages.filter((pkg) => !pkg.isActive);

  // Clear messages (you might want to add these to your context)
  const clearPackageError = () => {
    // Since error is managed in context, you might need to add a clear function there
    // For now, we'll rely on the auto-clear in the context
  };

  const clearPackageSuccess = () => {
    // Since success message is managed in context, you might need to add a clear function there
    // For now, we'll rely on the auto-clear in the context
  };

  // Loading State with Skeletons
  if (packageLoading && !refreshing && !initialLoadAttempted) {
    return (
      <div className={`p-4 sm:p-6 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}>
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 animate-pulse">
          <div className="w-full">
            <div
              className={`h-8 rounded w-3/4 sm:w-1/4 mb-3 ${isDark ? "bg-gray-700" : "bg-gray-300"}`}
            ></div>
            <div
              className={`h-4 rounded w-1/2 sm:w-1/3 ${isDark ? "bg-gray-700" : "bg-gray-300"}`}
            ></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div
              className={`h-10 rounded-lg w-full sm:w-32 ${isDark ? "bg-gray-700" : "bg-gray-300"}`}
            ></div>
            <div
              className={`h-10 rounded-lg w-full sm:w-32 ${isDark ? "bg-gray-700" : "bg-gray-300"}`}
            ></div>
          </div>
        </div>

        {/* Stats Skeletons */}
        <SkeletonAdminPackageStats theme={isDark ? "dark" : "light"} />

        {/* Search Skeleton */}
        <SkeletonAdminPackageSearch theme={isDark ? "dark" : "light"} />

        {/* Package Cards Skeletons */}
        <div className="space-y-4 sm:space-y-6">
          <SkeletonAdminPackageCard
            count={3}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    );
  }

  // Add error state display
  if (loadError && !packageLoading) {
    return (
      <div className={`p-4 sm:p-6 ${isDark ? "bg-gray-900" : "bg-gray-50"}`}>
        <div
          className={`p-6 rounded-lg border ${isDark ? "bg-gray-800 border-red-700" : "bg-white border-red-200"}`}
        >
          <div className="text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h3
              className={`text-lg font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Failed to Load Packages
            </h3>
            <p className={`mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {loadError}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleRetryLoad}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Create/Edit Form Loading State
  if (isCreating || editingPackage) {
    return (
      <div
        className={`p-4 sm:p-6 max-w-full mx-auto overflow-x-hidden transition-colors duration-300 ${
          isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
        }`}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="w-full">
            <h1
              className={`text-2xl sm:text-3xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Manage Packages
            </h1>
            <p
              className={`mt-1 text-sm sm:text-base ${
                isDark ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Create and manage whitelisted packages and job seeker packages
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors font-medium justify-center ${
                refreshing
                  ? "bg-gray-400 cursor-not-allowed"
                  : isDark
                    ? "bg-gray-700 text-white hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <svg
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
            <button
              onClick={() => setIsCreating(false)}
              className="bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium flex items-center gap-2 justify-center"
            >
              Back to List
            </button>
          </div>
        </div>

        {/* Form Skeleton */}
        {isCreating && !editingPackage && (
          <SkeletonAdminPackageForm theme={isDark ? "dark" : "light"} />
        )}

        {/* If editing, show actual form */}
        {editingPackage && (
          <PackageEditor
            pkg={editingPackage}
            onSave={async (updatedPackage) => {
              const result = await updatePackage(updatedPackage);
              if (result.success) {
                setEditingPackage(null);
              }
              return result;
            }}
            onCancel={() => setEditingPackage(null)}
            mode="edit"
            isDark={isDark}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`p-4 sm:p-6 max-w-full mx-auto overflow-x-hidden transition-colors duration-300 ${
        isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="w-full">
          <h1
            className={`text-2xl sm:text-3xl font-bold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Manage Packages
          </h1>
          <p
            className={`mt-1 text-sm sm:text-base ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Create and manage whitelisted packages and job seeker packages
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-colors font-medium justify-center ${
              refreshing
                ? "bg-gray-400 cursor-not-allowed"
                : isDark
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            <svg
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
          <button
            onClick={() => setIsCreating(true)}
            className="bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 justify-center"
          >
            <span>+</span>
            Create New Package
          </button>
        </div>
      </div>

      {/* Stats Overview - Show skeleton when loading */}
      {packageLoading && refreshing ? (
        <SkeletonAdminPackageStats theme={isDark ? "dark" : "light"} />
      ) : (
        packageStats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 sm:gap-4 mb-6">
            {[
              {
                label: "Total Packages",
                value: packageStats.totalPackages,
                color: "blue",
              },
              {
                label: "Active Packages",
                value: packageStats.activePackages,
                color: "green",
              },
              {
                label: "Deactivated Packages",
                value: deactivatedPackages.length,
                color: "red",
              },
              {
                label: "Popular Packages",
                value: packageStats.popularPackages,
                color: "yellow",
              },
              {
                label: "Total Subscriptions",
                value: packageStats.totalSubscriptions,
                color: "purple",
              },
            ].map((stat, index) => (
              <div
                key={index}
                className={`p-3 sm:p-4 rounded-lg shadow border ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <h3
                  className={`text-sm sm:text-lg font-semibold ${
                    isDark ? "text-gray-300" : "text-gray-900"
                  }`}
                >
                  {stat.label}
                </h3>
                <p
                  className={`text-xl sm:text-2xl font-bold ${
                    stat.color === "blue"
                      ? "text-blue-500"
                      : stat.color === "green"
                        ? "text-green-500"
                        : stat.color === "yellow"
                          ? "text-yellow-500"
                          : stat.color === "purple"
                            ? "text-purple-500"
                            : "text-red-500"
                  }`}
                >
                  {stat.value}
                </p>
              </div>
            ))}

            {/* Package Types */}
            <div
              className={`p-3 sm:p-4 rounded-lg shadow border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h3
                className={`text-sm sm:text-lg font-semibold ${
                  isDark ? "text-gray-300" : "text-gray-900"
                }`}
              >
                Package Types
              </h3>
              <div
                className={`text-xs sm:text-sm ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <div>
                  Whitelist Request:{" "}
                  {packageStats.packageTypes?.whitelist_request || 0}
                </div>
                <div>
                  Job Seeker: {packageStats.packageTypes?.job_seeker || 0}
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Search and Filters - Show skeleton when refreshing */}
      {packageLoading && refreshing ? (
        <SkeletonAdminPackageSearch theme={isDark ? "dark" : "light"} />
      ) : (
        <div
          className={`p-4 rounded-lg shadow border mb-6 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search packages by name, code, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
              />
            </div>
            <div className="w-full sm:w-48">
              <select
                value={packageTypeFilter}
                onChange={(e) => setPackageTypeFilter(e.target.value as any)}
                className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="all">All Types</option>
                <option value="whitelist_request">Whitelisted Request</option>
                <option value="job_seeker">Job Seeker</option>
              </select>
            </div>
            <div className="w-full sm:w-auto">
              <label className="flex items-center gap-2 p-2">
                <input
                  type="checkbox"
                  checked={showDeactivated}
                  onChange={(e) => setShowDeactivated(e.target.checked)}
                  className={`rounded focus:ring-blue-500 ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Show Deactivated Packages
                </span>
              </label>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <span
              className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              Showing {filteredPackages.length} of {safePackages.length}{" "}
              packages
              {showDeactivated &&
                ` (${deactivatedPackages.length} deactivated)`}
            </span>
          </div>
        </div>
      )}

      {/* Success Message Display */}
      {packageSuccessMessage && (
        <div
          className={`border rounded-lg p-4 mb-6 ${
            isDark
              ? "bg-green-900/30 border-green-800"
              : "bg-green-50 border-green-200"
          }`}
        >
          <div className="flex justify-between items-start">
            <div>
              <h3
                className={`font-semibold ${
                  isDark ? "text-green-300" : "text-green-800"
                }`}
              >
                Success
              </h3>
              <p
                className={`mt-1 text-sm ${
                  isDark ? "text-green-400" : "text-green-600"
                }`}
              >
                {packageSuccessMessage}
              </p>
            </div>
            <button
              onClick={clearPackageSuccess}
              className={`flex-shrink-0 ml-2 ${
                isDark
                  ? "text-green-400 hover:text-green-300"
                  : "text-green-600 hover:text-green-800"
              }`}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {packageError && (
        <div
          className={`border rounded-lg p-4 mb-6 ${
            isDark ? "bg-red-900/30 border-red-800" : "bg-red-50 border-red-200"
          }`}
        >
          <div className="flex justify-between items-start">
            <div>
              <h3
                className={`font-semibold ${
                  isDark ? "text-red-300" : "text-red-800"
                }`}
              >
                Error
              </h3>
              <p
                className={`mt-1 text-sm ${
                  isDark ? "text-red-400" : "text-red-600"
                }`}
              >
                {packageError}
              </p>
            </div>
            <button
              onClick={clearPackageError}
              className={`flex-shrink-0 ml-2 ${
                isDark
                  ? "text-red-400 hover:text-red-300"
                  : "text-red-600 hover:text-red-800"
              }`}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Packages Grid - Show skeleton when loading */}
      {packageLoading ? (
        <div className="space-y-4 sm:space-y-6">
          <SkeletonAdminPackageCard
            count={3}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {filteredPackages.map((pkg) => (
            <div
              key={pkg.id}
              className={`rounded-lg shadow border hover:shadow-md transition-shadow ${
                !pkg.isActive
                  ? isDark
                    ? "border-red-800 bg-red-900/20"
                    : "border-red-200 bg-red-50"
                  : isDark
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
              }`}
            >
              {editingPackage?.id === pkg.id ? (
                <PackageEditor
                  pkg={pkg}
                  onSave={updatePackage}
                  onCancel={() => setEditingPackage(null)}
                  mode="edit"
                  isDark={isDark}
                />
              ) : (
                <PackageView
                  pkg={pkg}
                  onEdit={() => setEditingPackage(pkg)}
                  onDelete={() => deletePackage(pkg.id)}
                  onDeactivate={() => deactivatePackage(pkg.id)}
                  onRestore={() => restorePackage(pkg.id)}
                  onToggleStatus={(isActive) =>
                    togglePackageStatus(pkg.id, isActive)
                  }
                  onTogglePopular={(isPopular) =>
                    togglePopularStatus(pkg.id, isPopular)
                  }
                  subscriptionCount={getSubscriptionCount(pkg.id)}
                  showDeactivated={showDeactivated}
                  isDark={isDark}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {safePackages.length === 0 && !isCreating && !packageLoading && (
        <div
          className={`text-center py-8 sm:py-12 rounded-lg border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
        >
          <div className="max-w-md mx-auto px-4">
            <div className="text-4xl sm:text-6xl mb-4">📦</div>
            <h3
              className={`text-lg sm:text-xl font-semibold mb-2 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {!Array.isArray(packages)
                ? "Error loading packages"
                : "No packages yet"}
            </h3>
            <p
              className={`mb-6 text-sm sm:text-base ${
                isDark ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {!Array.isArray(packages)
                ? "There was an issue loading packages. Please try refreshing."
                : "Create your first package to get started with your pricing plans."}
            </p>
            {Array.isArray(packages) && (
              <button
                onClick={() => setIsCreating(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium w-full sm:w-auto"
              >
                Create Your First Package
              </button>
            )}
          </div>
        </div>
      )}

      {/* No Search Results */}
      {safePackages.length > 0 &&
        filteredPackages.length === 0 &&
        !packageLoading && (
          <div
            className={`text-center py-8 sm:py-12 rounded-lg border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="max-w-md mx-auto px-4">
              <div className="text-4xl sm:text-6xl mb-4">🔍</div>
              <h3
                className={`text-lg sm:text-xl font-semibold mb-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                No packages found
              </h3>
              <p
                className={`text-sm sm:text-base ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                No packages match your search criteria. Try adjusting your
                search terms or filters.
              </p>
            </div>
          </div>
        )}
    </div>
  );
};

// Package View Component with Deactivate/Restore functionality
function PackageView({
  pkg,
  onEdit,
  onDelete,
  onDeactivate,
  onRestore,
  // onToggleStatus,
  onTogglePopular,
  subscriptionCount,
  // showDeactivated,
  isDark,
}: {
  pkg: any;
  onEdit: () => void;
  onDelete: () => void;
  onDeactivate: () => void;
  onRestore: () => void;
  onToggleStatus: (isActive: boolean) => void;
  onTogglePopular: (isPopular: boolean) => void;
  subscriptionCount: number;
  showDeactivated: boolean;
  isDark: boolean;
}) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getDurationText = (duration: number) => {
    if (duration === 7) return "7 Days";
    if (duration === 30) return "1 Month";
    if (duration === 90) return "3 Months";
    if (duration === 365) return "1 Year";
    return `${duration} days`;
  };

  // const formatDate = (dateString: string) => {
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //   });
  // };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div className="flex-1 w-full">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
            <h3
              className={`text-lg sm:text-xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {pkg.name}
            </h3>
            <div className="flex flex-wrap gap-1">
              <span
                className={`text-xs font-medium px-2 py-1 rounded ${
                  pkg.type === "whitelist_request"
                    ? isDark
                      ? "bg-purple-900/50 text-purple-300"
                      : "bg-purple-100 text-purple-800"
                    : isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-800"
                }`}
              >
                {pkg.type === "whitelist_request"
                  ? "Whitelist Request"
                  : "Job Seeker"}
              </span>
              {pkg.isPopular && (
                <span
                  className={`text-xs font-medium px-2 py-1 rounded ${
                    isDark
                      ? "bg-yellow-900/50 text-yellow-300"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  Popular
                </span>
              )}
              {!pkg.isActive && (
                <span
                  className={`text-xs font-medium px-2 py-1 rounded ${
                    isDark
                      ? "bg-red-900/50 text-red-300"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  Deactivated
                </span>
              )}
              <span
                className={`text-xs font-medium px-2 py-1 rounded ${
                  isDark
                    ? "bg-blue-900/50 text-blue-300"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {subscriptionCount} subscribers
              </span>
            </div>
          </div>
          <p
            className={`mb-2 text-sm sm:text-base ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {pkg.description}
          </p>
          <div
            className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            <span>Code: {pkg.code}</span>
            <span className="hidden sm:inline">•</span>
            <span>Order: #{pkg.orderIndex}</span>
            {pkg.type === "whitelist_request" && pkg.maxJobs && (
              <>
                <span className="hidden sm:inline">•</span>
                <span>
                  Max Jobs: {pkg.maxJobs === null ? "Unlimited" : pkg.maxJobs}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-start sm:justify-end">
          {!pkg.isActive ? (
            // Actions for deactivated packages
            <>
              <button
                onClick={onRestore}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex-1 sm:flex-none"
              >
                Restore
              </button>
              <button
                onClick={onEdit}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex-1 sm:flex-none"
              >
                Edit
              </button>
              <button
                onClick={onDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex-1 sm:flex-none"
              >
                Delete
              </button>
            </>
          ) : (
            // Actions for active packages
            <>
              <button
                onClick={onDeactivate}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none ${
                  isDark
                    ? "bg-red-900/50 text-red-300 hover:bg-red-800/50"
                    : "bg-red-100 text-red-800 hover:bg-red-200"
                }`}
              >
                Deactivate
              </button>
              <button
                onClick={() => onTogglePopular(!pkg.isPopular)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-none ${
                  pkg.isPopular
                    ? isDark
                      ? "bg-yellow-900/50 text-yellow-300 hover:bg-yellow-800/50"
                      : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    : isDark
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
              >
                {pkg.isPopular ? "Unmark Popular" : "Mark Popular"}
              </button>
              <button
                onClick={onEdit}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex-1 sm:flex-none"
              >
                Edit
              </button>
              <button
                onClick={onDelete}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex-1 sm:flex-none"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {pkg.isActive && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4">
            <div
              className={`p-4 rounded-lg ${
                isDark ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <h4
                className={`font-semibold mb-3 text-sm sm:text-base ${
                  isDark ? "text-gray-300" : "text-gray-900"
                }`}
              >
                Monthly Plan
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      isDark ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    Price:
                  </span>
                  <div className="text-right">
                    <span
                      className={`text-base sm:text-lg font-bold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatPrice(pkg.monthlyPrice)}
                    </span>
                    {pkg.originalMonthlyPrice &&
                      pkg.originalMonthlyPrice > pkg.monthlyPrice && (
                        <span
                          className={`line-through text-xs sm:text-sm ml-2 ${
                            isDark ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          {formatPrice(pkg.originalMonthlyPrice)}
                        </span>
                      )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      isDark ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    Duration:
                  </span>
                  <span
                    className={`text-sm sm:text-base ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {getDurationText(pkg.monthlyDuration)}
                  </span>
                </div>
              </div>
            </div>

            <div
              className={`p-4 rounded-lg ${
                isDark ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <h4
                className={`font-semibold mb-3 text-sm sm:text-base ${
                  isDark ? "text-gray-300" : "text-gray-900"
                }`}
              >
                Annual Plan
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      isDark ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    Price:
                  </span>
                  <div className="text-right">
                    <span
                      className={`text-base sm:text-lg font-bold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatPrice(pkg.annualPrice)}
                    </span>
                    {pkg.originalAnnualPrice &&
                      pkg.originalAnnualPrice > pkg.annualPrice && (
                        <span
                          className={`line-through text-xs sm:text-sm ml-2 ${
                            isDark ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          {formatPrice(pkg.originalAnnualPrice)}
                        </span>
                      )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      isDark ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    Duration:
                  </span>
                  <span
                    className={`text-sm sm:text-base ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {getDurationText(pkg.annualDuration)}
                  </span>
                </div>
                {pkg.annualDuration === 365 && (
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span
                      className={`font-medium ${
                        isDark ? "text-green-400" : "text-green-600"
                      }`}
                    >
                      Monthly equivalent:
                    </span>
                    <span
                      className={isDark ? "text-green-400" : "text-green-600"}
                    >
                      {formatPrice(Math.round(pkg.annualPrice / 12))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Whitelist Request-specific fields */}
          {pkg.type === "whitelist_request" && (
            <div
              className={`p-4 rounded-lg mb-4 ${
                isDark ? "bg-gray-700" : "bg-gray-50"
              }`}
            >
              <h4
                className={`font-semibold mb-3 text-sm sm:text-base ${
                  isDark ? "text-gray-300" : "text-gray-900"
                }`}
              >
                Whitelist Request Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      isDark ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    Max Jobs:
                  </span>
                  <span
                    className={`ml-2 text-sm sm:text-base ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {pkg.maxJobs === null ? "Unlimited" : pkg.maxJobs}
                  </span>
                </div>
                <div>
                  <span
                    className={`font-medium text-sm sm:text-base ${
                      isDark ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    Duration:
                  </span>
                  <span
                    className={`ml-2 text-sm sm:text-base ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {pkg.durationDays} days
                  </span>
                </div>
              </div>
            </div>
          )}

          <div>
            <h4
              className={`font-semibold mb-3 text-sm sm:text-base ${
                isDark ? "text-gray-300" : "text-gray-900"
              }`}
            >
              Features ({pkg.features.length})
            </h4>
            <ul className="space-y-2">
              {pkg.features.map((feature: any) => (
                <li
                  key={feature.id}
                  className={`flex items-start px-3 py-2 rounded text-sm sm:text-base ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-50 text-gray-700"
                  }`}
                >
                  <span className="text-green-500 mr-3 mt-0.5 flex-shrink-0">
                    ✓
                  </span>
                  <span className="break-words">{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

// Package Editor Component (keep the same as before)
function PackageEditor({
  pkg,
  onSave,
  onCancel,
  mode,
  isDark,
}: {
  pkg: any | null;
  onSave: (pkg: any) => Promise<{ success: boolean; error?: string }>;
  onCancel: () => void;
  mode: "create" | "edit";
  isDark: boolean;
}) {
  const [editedPackage, setEditedPackage] = useState<any>(
    pkg || {
      name: "",
      code: "",
      description: "",
      monthlyPrice: 0,
      annualPrice: 0,
      originalMonthlyPrice: undefined,
      originalAnnualPrice: undefined,
      monthlyDuration: 30,
      annualDuration: 365,
      isPopular: false,
      isActive: true,
      orderIndex: 0,
      features: [],
      type: "whitelist_request",
      maxJobs: 3,
      durationDays: 30,
    },
  );

  const [newFeature, setNewFeature] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const addFeature = () => {
    if (!newFeature.trim()) return;

    setEditedPackage((prev: any) => ({
      ...prev,
      features: [
        ...(prev.features || []),
        {
          id: Date.now().toString(),
          text: newFeature.trim(),
          orderIndex: prev.features?.length || 0,
        },
      ],
    }));
    setNewFeature("");
  };

  const removeFeature = (index: number) => {
    setEditedPackage((prev: any) => ({
      ...prev,
      features: prev.features?.filter((_: any, i: number) => i !== index) || [],
    }));
  };

  const updateFeature = (index: number, text: string) => {
    setEditedPackage((prev: any) => ({
      ...prev,
      features:
        prev.features?.map((feature: any, i: number) =>
          i === index ? { ...feature, text } : feature,
        ) || [],
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!editedPackage.name?.trim()) {
      newErrors.name = "Package name is required";
    }

    if (!editedPackage.code?.trim()) {
      newErrors.code = "Package code is required";
    } else if (!/^[a-z0-9_]+$/.test(editedPackage.code)) {
      newErrors.code =
        "Package code can only contain lowercase letters, numbers, and underscores";
    }

    if (
      editedPackage.monthlyPrice === undefined ||
      editedPackage.monthlyPrice < 0
    ) {
      newErrors.monthlyPrice = "Valid monthly price is required";
    }

    if (
      editedPackage.annualPrice === undefined ||
      editedPackage.annualPrice < 0
    ) {
      newErrors.annualPrice = "Valid annual price is required";
    }

    if (
      editedPackage.monthlyDuration === undefined ||
      editedPackage.monthlyDuration < 1
    ) {
      newErrors.monthlyDuration = "Valid monthly duration is required";
    }

    if (
      editedPackage.annualDuration === undefined ||
      editedPackage.annualDuration < 1
    ) {
      newErrors.annualDuration = "Valid annual duration is required";
    }

    if (editedPackage.type === "whitelist_request") {
      if (
        editedPackage.durationDays === undefined ||
        editedPackage.durationDays < 1
      ) {
        newErrors.durationDays =
          "Valid duration days is required for whitelist request packages";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const result = await onSave(editedPackage);
      if (result.success) {
        // Success is handled in the context
      } else if (result.error) {
        // Error is handled in the context
      }
    } catch (error) {
      // Error is handled in the context
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFeature();
    }
  };

  return (
    <div
      className={`p-4 sm:p-6 rounded-lg border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`}
    >
      <div className="flex justify-between items-center mb-4 sm:mb-6">
        <h2
          className={`text-xl sm:text-2xl font-bold ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          {mode === "create"
            ? "Create New Package"
            : `Edit Package: ${pkg?.name}`}
        </h2>
        <button
          onClick={onCancel}
          className={`text-lg ${
            isDark
              ? "text-gray-400 hover:text-gray-300"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          ✕
        </button>
      </div>

      <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
        {/* Basic Information */}
        <div
          className={`p-4 sm:p-6 rounded-lg shadow-sm border ${
            isDark ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
          }`}
        >
          <h3
            className={`text-lg font-semibold mb-4 ${
              isDark ? "text-gray-300" : "text-gray-900"
            }`}
          >
            Basic Information
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Package Type *
                </label>
                <select
                  value={editedPackage.type || "whitelist_request"}
                  onChange={(e) =>
                    setEditedPackage({
                      ...editedPackage,
                      type: e.target.value as
                        | "whitelist_request"
                        | "job_seeker",
                    })
                  }
                  className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                    isDark
                      ? "bg-gray-600 border-gray-500 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="whitelist_request">Whitelist Request</option>
                  <option value="job_seeker">Job Seeker</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Order Index
                </label>
                <input
                  type="number"
                  value={editedPackage.orderIndex || 0}
                  onChange={(e) =>
                    setEditedPackage({
                      ...editedPackage,
                      orderIndex: parseInt(e.target.value) || 0,
                    })
                  }
                  className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                    isDark
                      ? "bg-gray-600 border-gray-500 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  min="0"
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Package Name *
              </label>
              <input
                type="text"
                value={editedPackage.name || ""}
                onChange={(e) => {
                  setEditedPackage({
                    ...editedPackage,
                    name: e.target.value,
                  });
                  if (errors.name) setErrors({ ...errors, name: "" });
                }}
                className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                  errors.name
                    ? "border-red-300 focus:ring-red-500"
                    : isDark
                      ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                      : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                }`}
                placeholder="e.g., Premium Plan"
              />
              {errors.name && (
                <p className="text-red-600 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Package Code *
              </label>
              <input
                type="text"
                value={editedPackage.code || ""}
                onChange={(e) => {
                  setEditedPackage({
                    ...editedPackage,
                    code: e.target.value.toLowerCase(),
                  });
                  if (errors.code) setErrors({ ...errors, code: "" });
                }}
                className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                  errors.code
                    ? "border-red-300 focus:ring-red-500"
                    : isDark
                      ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                      : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                }`}
                placeholder="e.g., premium_plan"
              />
              {errors.code && (
                <p className="text-red-600 text-sm mt-1">{errors.code}</p>
              )}
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Use lowercase letters, numbers, and underscores only
              </p>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Description
              </label>
              <textarea
                value={editedPackage.description || ""}
                onChange={(e) =>
                  setEditedPackage({
                    ...editedPackage,
                    description: e.target.value,
                  })
                }
                className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                  isDark
                    ? "bg-gray-600 border-gray-500 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                rows={3}
                placeholder="Describe this package and its benefits..."
              />
            </div>

            {/* Whitelist Request-specific fields */}
            {editedPackage.type === "whitelist_request" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Max Jobs
                  </label>
                  <input
                    type="number"
                    value={
                      editedPackage.maxJobs === null
                        ? ""
                        : editedPackage.maxJobs
                    }
                    onChange={(e) =>
                      setEditedPackage({
                        ...editedPackage,
                        maxJobs:
                          e.target.value === ""
                            ? null
                            : parseInt(e.target.value),
                      })
                    }
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                      isDark
                        ? "bg-gray-600 border-gray-500 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    min="0"
                    placeholder="Leave empty for unlimited"
                  />
                  <p
                    className={`text-sm mt-1 ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Maximum number of jobs allowed (empty for unlimited)
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Duration Days *
                  </label>
                  <input
                    type="number"
                    value={editedPackage.durationDays || ""}
                    onChange={(e) => {
                      setEditedPackage({
                        ...editedPackage,
                        durationDays: parseInt(e.target.value),
                      });
                      if (errors.durationDays)
                        setErrors({ ...errors, durationDays: "" });
                    }}
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                      errors.durationDays
                        ? "border-red-300 focus:ring-red-500"
                        : isDark
                          ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                          : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                    }`}
                    min="1"
                  />
                  {errors.durationDays && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.durationDays}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editedPackage.isPopular || false}
                  onChange={(e) =>
                    setEditedPackage({
                      ...editedPackage,
                      isPopular: e.target.checked,
                    })
                  }
                  className={`rounded focus:ring-blue-500 h-5 w-5 ${
                    isDark
                      ? "bg-gray-600 border-gray-500 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Mark as Popular
                </span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={editedPackage.isActive !== false}
                  onChange={(e) =>
                    setEditedPackage({
                      ...editedPackage,
                      isActive: e.target.checked,
                    })
                  }
                  className={`rounded focus:ring-blue-500 h-5 w-5 ${
                    isDark
                      ? "bg-gray-600 border-gray-500 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Active Package
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Pricing & Features */}
        <div className="space-y-6">
          {/* Pricing */}
          <div
            className={`p-4 sm:p-6 rounded-lg shadow-sm border ${
              isDark
                ? "bg-gray-700 border-gray-600"
                : "bg-white border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDark ? "text-gray-300" : "text-gray-900"
              }`}
            >
              Pricing
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Monthly Price (KES) *
                  </label>
                  <input
                    type="number"
                    value={editedPackage.monthlyPrice || ""}
                    onChange={(e) => {
                      setEditedPackage({
                        ...editedPackage,
                        monthlyPrice: parseFloat(e.target.value),
                      });
                      if (errors.monthlyPrice)
                        setErrors({ ...errors, monthlyPrice: "" });
                    }}
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                      errors.monthlyPrice
                        ? "border-red-300 focus:ring-red-500"
                        : isDark
                          ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                          : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                    }`}
                    min="0"
                    step="1"
                  />
                  {errors.monthlyPrice && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.monthlyPrice}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Original Monthly Price (KES)
                  </label>
                  <input
                    type="number"
                    value={editedPackage.originalMonthlyPrice || ""}
                    onChange={(e) =>
                      setEditedPackage({
                        ...editedPackage,
                        originalMonthlyPrice:
                          parseFloat(e.target.value) || undefined,
                      })
                    }
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                      isDark
                        ? "bg-gray-600 border-gray-500 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Annual Price (KES) *
                  </label>
                  <input
                    type="number"
                    value={editedPackage.annualPrice || ""}
                    onChange={(e) => {
                      setEditedPackage({
                        ...editedPackage,
                        annualPrice: parseFloat(e.target.value),
                      });
                      if (errors.annualPrice)
                        setErrors({ ...errors, annualPrice: "" });
                    }}
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                      errors.annualPrice
                        ? "border-red-300 focus:ring-red-500"
                        : isDark
                          ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                          : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                    }`}
                    min="0"
                    step="1"
                  />
                  {errors.annualPrice && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.annualPrice}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Original Annual Price (KES)
                  </label>
                  <input
                    type="number"
                    value={editedPackage.originalAnnualPrice || ""}
                    onChange={(e) =>
                      setEditedPackage({
                        ...editedPackage,
                        originalAnnualPrice:
                          parseFloat(e.target.value) || undefined,
                      })
                    }
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                      isDark
                        ? "bg-gray-600 border-gray-500 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                    min="0"
                    step="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Monthly Duration (days) *
                  </label>
                  <input
                    type="number"
                    value={editedPackage.monthlyDuration || ""}
                    onChange={(e) => {
                      setEditedPackage({
                        ...editedPackage,
                        monthlyDuration: parseInt(e.target.value),
                      });
                      if (errors.monthlyDuration)
                        setErrors({ ...errors, monthlyDuration: "" });
                    }}
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                      errors.monthlyDuration
                        ? "border-red-300 focus:ring-red-500"
                        : isDark
                          ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                          : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                    }`}
                    min="1"
                  />
                  {errors.monthlyDuration && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.monthlyDuration}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Annual Duration (days) *
                  </label>
                  <input
                    type="number"
                    value={editedPackage.annualDuration || ""}
                    onChange={(e) => {
                      setEditedPackage({
                        ...editedPackage,
                        annualDuration: parseInt(e.target.value),
                      });
                      if (errors.annualDuration)
                        setErrors({ ...errors, annualDuration: "" });
                    }}
                    className={`border rounded-lg px-4 py-3 w-full focus:outline-none focus:ring-2 text-sm sm:text-base ${
                      errors.annualDuration
                        ? "border-red-300 focus:ring-red-500"
                        : isDark
                          ? "border-gray-500 focus:ring-blue-500 bg-gray-600 text-white"
                          : "border-gray-300 focus:ring-blue-500 bg-white text-gray-900"
                    }`}
                    min="1"
                  />
                  {errors.annualDuration && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.annualDuration}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          <div
            className={`p-4 sm:p-6 rounded-lg shadow-sm border ${
              isDark
                ? "bg-gray-700 border-gray-600"
                : "bg-white border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDark ? "text-gray-300" : "text-gray-900"
              }`}
            >
              Features ({editedPackage.features?.length || 0})
            </h3>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={handleFeatureKeyPress}
                  className={`border rounded-lg px-4 py-3 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                    isDark
                      ? "bg-gray-600 border-gray-500 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                  placeholder="Add a new feature (press Enter to add)"
                />
                <button
                  onClick={addFeature}
                  className="bg-green-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium whitespace-nowrap text-sm sm:text-base"
                >
                  Add Feature
                </button>
              </div>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {editedPackage.features?.map((feature: any, index: number) => (
                  <div
                    key={feature.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isDark
                        ? "bg-gray-600 border-gray-500"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <input
                      type="text"
                      value={feature.text}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      className={`flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-sm sm:text-base ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    />
                    <button
                      onClick={() => removeFeature(index)}
                      className={`p-1 rounded flex-shrink-0 ${
                        isDark
                          ? "text-red-400 hover:text-red-300"
                          : "text-red-600 hover:text-red-800"
                      }`}
                      title="Remove feature"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {(!editedPackage.features ||
                  editedPackage.features.length === 0) && (
                  <div
                    className={`text-center py-6 sm:py-8 ${
                      isDark ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    <div className="text-3xl sm:text-4xl mb-2">📝</div>
                    <p className="text-sm sm:text-base">
                      No features added yet
                    </p>
                    <p className="text-xs sm:text-sm mt-1">
                      Add features that describe what users get with this
                      package
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 sm:pt-6 border-t border-gray-200">
        <button
          onClick={onCancel}
          disabled={saving}
          className="bg-gray-500 text-white px-6 sm:px-8 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium w-full sm:w-auto text-sm sm:text-base disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-6 sm:px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium w-full sm:w-auto text-sm sm:text-base disabled:opacity-50"
        >
          {saving
            ? "Saving..."
            : mode === "create"
              ? "Create Package"
              : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

export default AdminPackagesManager;
